import React, { useRef, useEffect, FC } from 'react'
import withPortal from 'src/hoc/withPortal'
import withRuntime from 'theme/hoc/withRuntime'
import { XIcon } from '@heroicons/react/solid'

export interface ModalProps extends React.PropsWithChildren {
  isVisible: boolean
  isProcessing?: boolean
  closeOnOutsideClick?: boolean
  maxWidth?: string
  onClose: (message?: string) => void
}

// Modal done right way ;)
const GenericModal: FC<ModalProps> = ({ children, isVisible, closeOnOutsideClick = true, onClose }) => {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!overlayRef.current) {
      return
    }

    if (isVisible) {
      overlayRef.current.style.display = 'flex'
      // Workaround for opacity - tailwind way not working for some reason
      overlayRef.current.style.background = 'rgba(0, 0, 0, 0.5)'
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
      const timer = setTimeout(() => {
        if (!overlayRef.current) {
          return
        }

        overlayRef.current.style.display = 'none'
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isVisible])

  const handleClose = () => {
    document.body.style.overflow = 'unset'
    onClose()
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (closeOnOutsideClick && overlayRef.current && overlayRef.current === event.target) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [overlayRef])

  return (
    <div
      className="fixed inset-0 hidden flex-col justify-center items-center overflow-hidden py-6 px-4 z-50"
      ref={overlayRef}
    >
      {isVisible && (
        <div className="w-full md:w-1/2 lg:w-2/5 mx-auto my-0 flex flex-col bg-white rounded-lg shadow-lg overflow-y-auto relative">
          <XIcon className="absolute h-6 w-6 top-3.5 right-3.5 z-10 cursor-pointer" onClick={handleClose} />
          {children}
        </div>
      )}
    </div>
  )
}

export default withRuntime(withPortal(GenericModal))
